import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { ClipboardList, CircleDollarSign, Package, Wrench } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { Botao } from '@/componentes/Botao';
import { CabecalhoPagina } from '@/componentes/CabecalhoPagina';
import { Campo, CampoSelecao, CampoTexto } from '@/componentes/Campo';
import { CampoDeData } from '@/componentes/CampoDeData';
import { CardIndicador } from '@/componentes/CardIndicador';
import { Barra, Carregando } from '@/componentes/Esqueleto';
import { ErroDaLista } from '@/componentes/ErroDaLista';
import { EstadoVazio } from '@/componentes/EstadoVazio';
import { FaixaIndicadores } from '@/componentes/FaixaIndicadores';
import { Janela, JanelaDeAcao } from '@/componentes/JanelaDeAcao';
import { ListaDeEscolha } from '@/componentes/ListaDeEscolha';
import { MaisAcoes, type AcaoDoMenu } from '@/componentes/MaisAcoes';
import { Medida } from '@/componentes/Medida';
import { rotuloDaEscolha } from '@/componentes/rotuloDaEscolha';
import { RodapeTabela, Tabela, TextoDaCelula, type Coluna } from '@/componentes/Tabela';
import { HistoriaNaMesa } from '@/historias/mesa';
import { usePaginacao } from '@/modulos/operacao/paginacao';
import { formatarAteDuasCasas, formatarInteiro, formatarReais, interpretarNumero, formatarDia } from '@/lib/formato';
import { explicarFalha } from '@/lib/pedido';
import { retornoDe } from '@/lib/retorno';
import { OPCOES_POR_PAGINA, PADRAO_POR_PAGINA, fatiarPagina } from '@/modulos/painel/painel';
import { api as apiDoEstoque } from '@/modulos/estoque/api';
import { ajudaDaConversao, textoDaConversao } from '@/modulos/estoque/conversao';
import { faltaDoDeposito, opcaoVaziaDoDeposito } from '@/modulos/estoque/deposito';
import { custoSugerido, depositoSugerido, opcaoDoItem, prazoDoFornecedor, proximoNumeroDePedido, totalDaLinha, ultimaEntrada } from '@/modulos/estoque/preenchimento';
import { SeloSemAplicacao } from '@/modulos/estoque/SeloSemAplicacao';
import { useUltimoUsado } from '@/modulos/estoque/useUltimoUsado';
import { numeroDaOrdem } from '@/modulos/oficina/ordem';
import { useSessao } from '@/modulos/sessao/consultas';

import { api, type AlcadaDoPedido, type ItemDaNecessidade, type OrigemDaRequisicao, type Pedido } from './api';
import {
  deOndeVeioADemanda,
  estadoDaFaixa,
  paginaDoPedido,
  origemEscrita,
  porqueNaoDa,
  previsaoEscrita,
  previsaoNaDemanda,
  situacaoEscrita,
  totaisDaCompra,
} from './compras';

/** As cinco origens, na ordem em que a tela as oferece. */
const ORIGENS: readonly OrigemDaRequisicao[] = ['revisao', 'ordem_servico', 'minimo', 'consumo', 'manual'];

/**
 * MÓDULO 09 — compras e reposição. O segundo do ciclo 2.
 *
 * ## A tela abre na subtração, e não na lista de pedidos
 *
 * O v6 escreve o risco do módulo em três: *"fretes emergenciais, compras
 * DUPLICADAS e parada por falta de peça"*. A duplicada é a única das três que o
 * software causa sozinho, e ela nasce de uma tela que mostra falta sem
 * descontar o que já foi pedido: a mesma falta de 40 litros aparece toda
 * semana, inclusive depois de alguém comprar os 40.
 *
 * Então a primeira coisa na tela é a conta:
 *
 *     comprar = demanda − disponível − pedido já aberto
 *
 * Ela vem resolvida do banco, pela view `compras.necessidade_liquida`, e a
 * frase que a explica vem com ela — não escrita aqui. Duas cópias da mesma
 * conta divergem na primeira correção, e aqui a divergência tem nome.
 *
 * ## Rascunho não desconta, e isso está escrito na tela
 *
 * Pedido em rascunho não é compromisso. Se ele descontasse, a falta sumiria da
 * lista sem nada ter sido pedido — o erro simétrico, e pior, porque a peça não
 * chega. A coluna "Em pedido" só conta aprovado e emitido.
 *
 * ## A ação primária é montar o pedido
 *
 * Uma só em laranja, como o manual manda. Aprovar está no menu da linha e é
 * outra licença: o v6 pede "controle sobre o compromisso de compra", e quem
 * monta o pedido propõe — o aprovador decide.
 */

type Pendente =
  | { readonly tipo: 'aprovar'; readonly pedido: Pedido }
  | { readonly tipo: 'ajuste'; readonly pedido: Pedido }
  | { readonly tipo: 'item'; readonly pedido: Pedido }
  | { readonly tipo: 'cancelar'; readonly pedido: Pedido }
  | { readonly tipo: 'receber'; readonly pedido: Pedido };

/** Por que não se acrescenta item a este pedido, ou `undefined` quando se pode (pedido.gravar, antes de aprovar). */
function porqueNaoAcrescenta(pedido: Pedido, permissoes: readonly string[]): string | undefined {
  if (!permissoes.includes('pedido.gravar')) return 'Você não tem a licença pedido.gravar. Peça a liberação em Cadastros · Governança.';
  if (pedido.situacao !== 'rascunho' && pedido.situacao !== 'aguardando aprovacao') {
    return 'Item se acrescenta só antes de aprovar. Este pedido já passou dessa etapa.';
  }
  return undefined;
}

/**
 * A alçada de compra de quem está logado, escrita (0079; manual p.44, Aprovação
 * e trilha: a tela mostra a alçada antes do botão de aprovar). É a mesma frase
 * do app (TelaPedido, M23).
 */
function textoDaAlcada(alcada: AlcadaDoPedido): string {
  if (!alcada.regra) return 'A empresa não cadastrou alçada de compra: aprova quem tem a licença.';
  if (alcada.semTeto) return 'Sua alçada de compra não tem teto.';
  if (alcada.limite !== null) return `Sua alçada de compra vai até ${formatarReais(alcada.limite)}.`;
  return 'Você não tem alçada de compra cadastrada.';
}

const rota = getRouteApi('/compras');

export function PaginaCompras() {
  const base = useId();
  /* O pedido que o endereço abre (`?pedido=`): o aviso de pedido atrasado
     (0091) leva a ele. A linha fica marcada, na página certa e à vista. */
  const pedidoDoEndereco = rota.useSearch().pedido ?? null;
  const quadroDosPedidos = useRef<HTMLDivElement>(null);
  const cliente = useQueryClient();
  const sessao = useSessao();
  const permissoes = useMemo(() => sessao.data?.permissoes ?? [], [sessao.data]);
  /* Cada ação só para quem tem a licença dela (prova do marco, 07/10/2026):
     antes elas apareciam desligadas, com "Você não tem a licença" no menu. */
  const podeRequisitar = permissoes.includes('requisicao.gravar');
  const podeMontarPedido = permissoes.includes('pedido.gravar');
  const podeAprovarPedido = permissoes.includes('pedido.aprovar');
  const podeReceberPedido = permissoes.includes('recebimento.gravar');

  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(PADRAO_POR_PAGINA);
  const [montando, setMontando] = useState(false);
  /* `null` = segue o que o sistema propõe (o próximo número, o prazo que o
     fornecedor deu da última vez); o texto é o que a pessoa escreveu. */
  const [numeroDigitado, setNumeroDigitado] = useState<string | null>(null);
  const [fornecedorId, setFornecedorId] = useState('');
  const [prazoDigitado, setPrazoDigitado] = useState<string | null>(null);
  const [pendente, setPendente] = useState<Pendente | null>(null);
  const [motivo, setMotivo] = useState('');
  /* A requisição à mão: a ferramenta 1 do v6, para a necessidade que não vem
     do mínimo nem da preventiva — o encarregado que viu a peça gastando. */
  const [requisitando, setRequisitando] = useState(false);
  const [reqProdutoId, setReqProdutoId] = useState('');
  const [reqQuantidade, setReqQuantidade] = useState('');
  const [reqOrigem, setReqOrigem] = useState<string>('manual');
  const [reqOrdemId, setReqOrdemId] = useState('');
  const [reqJustificativa, setReqJustificativa] = useState('');
  const [reqPrecisaAte, setReqPrecisaAte] = useState('');
  const [itemEscolhidoId, setItemId] = useState('');
  /* `null` = o que falta chegar do item (na unidade do item). */
  const [quantidadeDigitada, setQuantidade] = useState<string | null>(null);
  /* 0088 · a embalagem em que o item chegou; vazio é a unidade do item. */
  const [conversaoRecebida, setConversaoRecebida] = useState('');
  const [documento, setDocumento] = useState('');
  const [custoUnitario, setCustoUnitario] = useState('');
  /* Acrescentar item ao pedido: o item do estoque, o depósito onde entra ao chegar, a quantidade e o preço. */
  const [itemProdutoId, setItemProdutoId] = useState('');
  /* `null` = o que o sistema propõe: o depósito de sempre do item, o que a
     necessidade manda comprar, o custo da última entrada. */
  const [itemDepositoEscolhido, setItemDepositoId] = useState<string | null>(null);
  const [itemQuantidadeDigitada, setItemQuantidade] = useState<string | null>(null);
  const [itemPrecoDigitado, setItemPreco] = useState<string | null>(null);
  const [ultimoDeposito, guardarDeposito] = useUltimoUsado('estoque.deposito');

  const necessidade = useQuery({ queryKey: ['compras', 'necessidade'], queryFn: api.necessidade });
  const pedidos = useQuery({ queryKey: ['compras', 'pedidos'], queryFn: api.pedidos });

  const itens = useMemo(() => necessidade.data?.itens ?? [], [necessidade.data]);
  const listaDePedidos = useMemo(() => pedidos.data?.pedidos ?? [], [pedidos.data]);
  const paginaDosPedidos = usePaginacao(listaDePedidos);
  const irParaPaginaDosPedidos = paginaDosPedidos.rodape.aoMudarPagina;
  const porPaginaDosPedidos = paginaDosPedidos.rodape.porPagina;
  const paginaDoAberto = pedidoDoEndereco === null ? null : paginaDoPedido(listaDePedidos, pedidoDoEndereco, porPaginaDosPedidos);
  useEffect(() => {
    if (paginaDoAberto === null) return;
    irParaPaginaDosPedidos(paginaDoAberto);
    /* Depois de a página trocar: a linha marcada (ou o cartão, abaixo de 768 px) vem para o centro. */
    const quadro = quadroDosPedidos.current;
    requestAnimationFrame(() => {
      quadro?.querySelector('[aria-selected="true"], [aria-current="true"]')?.scrollIntoView({ block: 'center' });
    });
  }, [paginaDoAberto, irParaPaginaDosPedidos]);
  const totais = useMemo(() => totaisDaCompra(itens, listaDePedidos), [itens, listaDePedidos]);
  const faixa = estadoDaFaixa(necessidade);
  const fatia = fatiarPagina(itens, pagina, porPagina);

  const recarregar = () => cliente.invalidateQueries({ queryKey: ['compras'] });
  const fecharPendente = () => {
    setPendente(null);
    setMotivo('');
    setItemId('');
    setQuantidade(null);
    setConversaoRecebida('');
    setDocumento('');
    setCustoUnitario('');
    setItemProdutoId('');
    setItemDepositoId(null);
    setItemQuantidade(null);
    setItemPreco(null);
  };

  /* O "em quê" do retorno (R04, p.32): o número do pedido e o fornecedor. */
  const doPedido = (id: string) => {
    const p = listaDePedidos.find((x) => x.id === id);
    return p ? { alvo: { codigo: `Pedido ${p.numero}`, nome: p.fornecedor }, pedido: p } : { pedido: undefined };
  };

  const criarPedido = useMutation({
    meta: {
      retorno: retornoDe(() => ({
        titulo: 'Pedido de compra criado',
        alvo: { codigo: `Pedido ${numero}` },
        fatos: [prazoDias.trim() ? `prazo de ${prazoDias.trim()} dias` : null],
        efeito: 'Fica em rascunho: acrescente os itens e mande aprovar',
      })),
    },
    mutationFn: () => {
      const prazo = prazoDias.trim() === '' ? null : interpretarNumero(prazoDias);
      return api.criarPedido({ numero, fornecedorId, prazoDias: prazo });
    },
    onSuccess: async (criado) => {
      setMontando(false);
      setNumeroDigitado(null);
      setFornecedorId('');
      setPrazoDigitado(null);
      await recarregar();
      /* O PRÓXIMO PASSO JÁ ABERTO: o pedido nasce vazio, e o que se faz com
         ele em seguida é acrescentar os itens. A janela de acrescentar abre
         sozinha no pedido novo, em vez de mandar a pessoa achar a linha e o
         menu dela. */
      const novo = cliente.getQueryData<{ pedidos: readonly Pedido[] }>(['compras', 'pedidos'])?.pedidos.find((p) => p.id === criado.id);
      if (novo && podeMontarPedido) setPendente({ tipo: 'item', pedido: novo });
    },
  });
  const requisitar = useMutation({
    meta: {
      retorno: retornoDe(() => {
        const item = itens.find((i) => i.produtoId === reqProdutoId);
        return {
          titulo: 'Requisição de compra aberta',
          ...(item ? { alvo: { codigo: item.codigo, nome: item.produto } } : {}),
          fatos: [
            `${formatarAteDuasCasas(interpretarNumero(reqQuantidade) ?? 0)}${item ? ` ${item.unidade}` : ''}`,
            reqPrecisaAte.trim() ? `para ${formatarDia(reqPrecisaAte)}` : null,
          ],
        };
      }),
    },
    mutationFn: () =>
      api.criarRequisicao({
        produtoId: reqProdutoId,
        quantidade: interpretarNumero(reqQuantidade) ?? 0,
        origem: reqOrigem as OrigemDaRequisicao,
        /* A origem `ordem_servico` SEM a O.S. é recusada pelo banco: origem que
           não se pode conferir não é origem. */
        ordemId: reqOrdemId.trim() === '' ? null : reqOrdemId,
        justificativa: reqJustificativa,
        precisaAte: reqPrecisaAte.trim() === '' ? null : reqPrecisaAte,
      }),
    onSuccess: async () => {
      setRequisitando(false);
      setReqProdutoId('');
      setReqQuantidade('');
      setReqJustificativa('');
      setReqPrecisaAte('');
      await recarregar();
    },
  });
  const aprovar = useMutation({
    mutationFn: api.aprovar,
    meta: {
      retorno: retornoDe<unknown, string>((_r, id) => {
        const { alvo, pedido } = doPedido(id);
        return { titulo: 'Pedido de compra aprovado', ...(alvo ? { alvo } : {}), fatos: [pedido ? formatarReais(pedido.valorEstimado) : null] };
      }),
    },
    onSuccess: async () => {
      fecharPendente();
      await recarregar();
    },
  });
  /* Acima da alçada de quem está logado, o pedido vai a quem pode aprovar
     (0079): o sino avisa. "Pedir ajuste" devolve ao rascunho com o motivo. */
  const enviar = useMutation({
    mutationFn: api.enviar,
    meta: {
      retorno: retornoDe<unknown, string>((_r, id) => {
        const { alvo, pedido } = doPedido(id);
        return {
          titulo: 'Pedido de compra enviado para aprovar',
          ...(alvo ? { alvo } : {}),
          fatos: [pedido ? formatarReais(pedido.valorEstimado) : null],
          efeito: 'O sino avisa quem tem alçada de compra',
        };
      }),
    },
    onSuccess: async () => {
      fecharPendente();
      await recarregar();
    },
  });
  const ajustar = useMutation({
    mutationFn: (p: { id: string; motivo: string }) => api.pedirAjuste(p.id, p.motivo),
    meta: {
      retorno: retornoDe<unknown, { id: string; motivo: string }>((_r, p) => {
        const { alvo } = doPedido(p.id);
        return {
          titulo: 'Pedido de compra devolvido para ajuste',
          ...(alvo ? { alvo } : {}),
          fatos: [`motivo: ${p.motivo}`],
          efeito: 'Voltou ao rascunho de quem pediu',
        };
      }),
    },
    onSuccess: async () => {
      fecharPendente();
      await recarregar();
    },
  });
  const acrescentar = useMutation({
    mutationFn: (p: { pedidoId: string; produtoId: string; depositoId: string; quantidade: number; preco: number | null }) =>
      api.acrescentarItem(p.pedidoId, {
        produtoId: p.produtoId,
        depositoId: p.depositoId,
        quantidade: p.quantidade,
        precoUnitario: p.preco,
      }),
    meta: {
      retorno: retornoDe<unknown, { pedidoId: string; produtoId: string; depositoId: string; quantidade: number; preco: number | null }>((_r, p) => {
        const { alvo } = doPedido(p.pedidoId);
        const produto = apoioDoEstoque.data?.produtos.find((x) => x.id === p.produtoId);
        const deposito = apoioDoEstoque.data?.depositos.find((x) => x.id === p.depositoId);
        return {
          titulo: 'Item acrescentado ao pedido',
          ...(alvo ? { alvo } : {}),
          fatos: [
            produto ? `${formatarAteDuasCasas(p.quantidade)} ${produto.unidade} de ${produto.nome}` : formatarAteDuasCasas(p.quantidade),
            p.preco === null ? 'sem preço' : `${formatarReais(p.preco)} por unidade`,
            deposito ? `entra em ${deposito.nome}` : null,
          ],
          efeito: 'O valor estimado do pedido sobe; ele continua esperando aprovação',
        };
      }),
    },
    onSuccess: async (_resposta, p) => {
      /* O depósito em que o item entrou vira o "último usado" desta pessoa. */
      guardarDeposito(p.depositoId);
      fecharPendente();
      await recarregar();
    },
  });
  const emitir = useMutation({
    mutationFn: api.emitir,
    meta: {
      retorno: retornoDe<unknown, string>((_r, id) => {
        const { alvo, pedido } = doPedido(id);
        return {
          titulo: 'Pedido de compra emitido',
          ...(alvo ? { alvo } : {}),
          fatos: [pedido ? `${formatarInteiro(pedido.itens)} ${pedido.itens === 1 ? 'item' : 'itens'}` : null, pedido ? formatarReais(pedido.valorEstimado) : null],
          efeito: 'Os itens passam a contar como em pedido na necessidade',
        };
      }),
    },
    onSuccess: recarregar,
  });
  const cancelar = useMutation({
    mutationFn: (p: { id: string; motivo: string }) => api.cancelar(p.id, p.motivo),
    meta: {
      retorno: retornoDe<unknown, { id: string; motivo: string }>((_r, p) => {
        const { alvo } = doPedido(p.id);
        return { titulo: 'Pedido de compra cancelado', ...(alvo ? { alvo } : {}), fatos: [`motivo: ${p.motivo}`] };
      }),
    },
    onSuccess: async () => {
      fecharPendente();
      await recarregar();
    },
  });
  const receber = useMutation({
    mutationFn: (p: { itemId: string; quantidade: number; documento: string; custo: number | null; conversaoId: string | null }) =>
      api.receber(p.itemId, {
        quantidade: p.quantidade,
        documento: p.documento,
        custoUnitario: p.custo,
        ...(p.conversaoId ? { conversaoId: p.conversaoId } : {}),
      }),
    meta: {
      retorno: retornoDe<
        { pedidoCompleto: boolean; quantidade?: number | null },
        { itemId: string; quantidade: number; documento: string; custo: number | null; conversaoId: string | null }
      >(
        (r, p) => {
          const item = itensDoPedidoPendente.data?.itens.find((i) => i.itemId === p.itemId);
          const pedido = pendente?.tipo === 'receber' ? pendente.pedido : undefined;
          /* 0088 · na embalagem, o retorno diz as duas: o que chegou e o que entrou no saldo. */
          const embalagem = item?.conversoes?.find((c) => c.id === p.conversaoId);
          return {
            titulo: 'Recebimento registrado',
            ...(pedido ? { alvo: { codigo: `Pedido ${pedido.numero}`, nome: pedido.fornecedor } } : {}),
            fatos: [
              item && embalagem
                ? `${formatarAteDuasCasas(p.quantidade)} ${embalagem.unidade} = ${formatarAteDuasCasas(r.quantidade ?? p.quantidade * embalagem.fator)} ${item.unidade} de ${item.produto}`
                : item
                  ? `${formatarAteDuasCasas(p.quantidade)} ${item.unidade} de ${item.produto}`
                  : formatarAteDuasCasas(p.quantidade),
              p.documento ? `documento ${p.documento}` : null,
              p.custo === null ? null : `${formatarReais(p.custo)} por ${embalagem?.unidade ?? 'unidade'}`,
            ],
            efeito: r.pedidoCompleto ? 'O pedido ficou completo; entrou no estoque' : 'Entrou no estoque; o pedido ainda tem itens a chegar',
          };
        },
      ),
    },
    onSuccess: async () => {
      fecharPendente();
      await recarregar();
    },
  });

  /* ------------------------------------------------- a tabela da necessidade */

  const quantia = (valor: number, unidade: string) => (
    <span className="whitespace-nowrap">
      <Medida valor={formatarAteDuasCasas(valor)} unidade={unidade} />
    </span>
  );

  /**
   * SEIS colunas, e a quarta é a razão do módulo. "Em pedido" ao lado de
   * "Disponível" e antes de "Comprar" deixa a subtração visível na linha: sem
   * ela, "comprar 0" com demanda 10 se leria como defeito.
   */
  const COLUNAS: readonly Coluna<ItemDaNecessidade>[] = [
    {
      chave: 'produto',
      rotulo: 'Item',
      celula: (i) => (
        <span className="flex min-w-0 flex-col items-start">
          <span className="codigo text-tinta">{i.codigo}</span>
          <TextoDaCelula
            texto={`${i.produto} · ${deOndeVeioADemanda(i)}${i.critico ? ' · item crítico' : ''}`}
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
          {/* R06 (p.71): o selo âmbar também na necessidade — aviso, nunca bloqueio. */}
          {i.semAplicacaoValidada ? (
            <span className="pt-1">
              <SeloSemAplicacao />
            </span>
          ) : null}
        </span>
      ),
    },
    {
      chave: 'demanda',
      rotulo: 'Demanda',
      alinhamento: 'numero',
      /* A previsão de consumo (0091) traz a conta escrita na segunda linha:
         "previsão" sem a média e o prazo seria um número sem procedência. */
      celula: (i) => {
        const previsao = previsaoEscrita(i);
        const curta = previsaoNaDemanda(i);
        return previsao === null || curta === null ? (
          quantia(i.demanda, i.unidade)
        ) : (
          /* A conta da previsão como segunda linha da Demanda (R06, p.71):
             "3,2 un/mês × 2,5 meses"; a conta inteira, em dias, no `title`. */
          <span className="flex flex-col items-end">
            {quantia(i.demanda, i.unidade)}
            <span className="txt-apoio whitespace-nowrap text-tinta-secundaria tabular-nums" title={`Previsão de consumo: ${previsao}`}>
              {curta}
            </span>
          </span>
        );
      },
    },
    {
      chave: 'disponivel',
      rotulo: 'Disponível',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (i) => quantia(i.disponivel, i.unidade),
    },
    {
      chave: 'emPedido',
      rotulo: 'Em pedido',
      alinhamento: 'numero',
      /* A parcela que o módulo 09 trouxe. Zero não é alerta: é a informação de
         que ninguém pediu ainda. */
      celula: (i) => quantia(i.emPedido, i.unidade),
    },
    {
      chave: 'comprar',
      rotulo: 'Comprar',
      historia: 'COL-COM-05',
      alinhamento: 'numero',
      celula: (i) =>
        /* Colorido SÓ quando o próprio valor é o alerta (regra 3), e só para o
           item crítico: tudo vermelho deixa de ser aviso. */
        i.comprar > 0 && i.critico ? (
          <span className="numero whitespace-nowrap text-parada-texto">
            {formatarAteDuasCasas(i.comprar)}
          </span>
        ) : (
          quantia(i.comprar, i.unidade)
        ),
    },
    {
      chave: 'precisaAte',
      rotulo: 'Precisa até',
      prioridade: 'secundaria',
      celula: (i) =>
        i.precisaAte === null ? (
          <span className="text-tinta-secundaria">—</span>
        ) : (
          <span className="numero whitespace-nowrap text-tinta">
            {formatarDia(i.precisaAte)}
          </span>
        ),
    },
  ];

  /* ----------------------------------------------------- a tabela de pedidos */

  const COLUNAS_DE_PEDIDO: readonly Coluna<Pedido>[] = [
    {
      chave: 'numero',
      rotulo: 'Pedido',
      celula: (p) => (
        <span className="flex min-w-0 flex-col">
          <span className="codigo text-tinta">{p.numero}</span>
          <TextoDaCelula
            texto={`${p.fornecedor}${p.prazoDias === null ? '' : ` · ${formatarInteiro(p.prazoDias)} dias de prazo`}`}
            classe="txt-apoio text-tinta-apagada"
            linhas={1}
          />
        </span>
      ),
    },
    {
      chave: 'situacao',
      rotulo: 'Situação',
      celula: (p) => <span className="txt-corpo text-tinta">{situacaoEscrita(p.situacao)}</span>,
    },
    {
      chave: 'itens',
      rotulo: 'Itens',
      alinhamento: 'numero',
      prioridade: 'secundaria',
      celula: (p) => <span className="numero text-tinta">{formatarInteiro(p.itens)}</span>,
    },
    {
      chave: 'valor',
      rotulo: 'Valor estimado',
      alinhamento: 'numero',
      celula: (p) => <span className="numero whitespace-nowrap text-tinta">{formatarReais(p.valorEstimado)}</span>,
    },
    {
      chave: 'falta',
      rotulo: 'Falta chegar',
      alinhamento: 'numero',
      celula: (p) =>
        p.faltaChegar > 0 ? (
          <span className="numero whitespace-nowrap text-tinta">{formatarAteDuasCasas(p.faltaChegar)}</span>
        ) : (
          <span className="text-tinta-secundaria">—</span>
        ),
    },
  ];

  const acoesDoPedido = (p: Pedido): readonly AcaoDoMenu[] => {
    const monte = (
      tipo: 'aprovar' | 'ajuste' | 'emitir' | 'cancelar' | 'receber',
      rotulo: string,
      aoEscolher: () => void,
    ): AcaoDoMenu => {
      /* Pedir ajuste é de quem aprova, e só antes de aprovar: as mesmas regras de aprovar. */
      const motivoIndisponivel = porqueNaoDa(p, tipo === 'ajuste' ? 'aprovar' : tipo, permissoes);
      const historia = tipo === 'aprovar' ? { historia: 'BTN-COM-07' } : {};
      return motivoIndisponivel === undefined ? { rotulo, aoEscolher, ...historia } : { rotulo, aoEscolher, motivoIndisponivel, ...historia };
    };
    /* Só as ações da licença de quem está logado; o que ainda não cabe pela
       situação do pedido continua no menu, desligado e com o porquê. */
    return [
      /* Aprovar abre a janela com a alçada de quem está logado ANTES do botão
         (p.44): acima dela, o botão vira "Enviar para aprovação". */
      ...(podeAprovarPedido
        ? [
            monte('aprovar', 'Aprovar pedido', () => setPendente({ tipo: 'aprovar', pedido: p })),
            monte('ajuste', 'Pedir ajuste', () => setPendente({ tipo: 'ajuste', pedido: p })),
          ]
        : []),
      ...(podeMontarPedido
        ? [
            (() => {
              const motivoIndisponivel = porqueNaoAcrescenta(p, permissoes);
              const aoEscolher = () => setPendente({ tipo: 'item', pedido: p });
              return motivoIndisponivel === undefined
                ? { rotulo: 'Acrescentar item', aoEscolher }
                : { rotulo: 'Acrescentar item', aoEscolher, motivoIndisponivel };
            })(),
            monte('emitir', 'Emitir pedido', () => emitir.mutate(p.id)),
          ]
        : []),
      ...(podeReceberPedido ? [monte('receber', 'Registrar recebimento', () => setPendente({ tipo: 'receber', pedido: p }))] : []),
      ...(podeAprovarPedido ? [monte('cancelar', 'Cancelar pedido', () => setPendente({ tipo: 'cancelar', pedido: p }))] : []),
    ];
  };

  const itensDoPedidoPendente = useQuery({
    queryKey: ['compras', 'itens', pendente?.tipo === 'receber' || pendente?.tipo === 'aprovar' ? pendente.pedido.id : null],
    queryFn: () => api.itensDoPedido((pendente as { readonly pedido: Pedido }).pedido.id),
    enabled: pendente?.tipo === 'receber' || pendente?.tipo === 'aprovar',
  });
  /* Os itens do estoque e os depósitos, só com a janela de acrescentar aberta. A chave é a do estoque: o cache é um só.
     A mesma lista dá o fornecedor do pedido e o item e a O.S. da necessidade, que
     pediam o identificador interno (achado no QAS, 20/10/2026) — nessas duas
     janelas, só para quem lê o estoque. */
  const podeLerEstoque = permissoes.includes('estoque.ler');
  const apoioDoEstoque = useQuery({
    queryKey: ['estoque', 'apoio'],
    queryFn: () => apiDoEstoque.apoio(),
    enabled: pendente?.tipo === 'item' || (podeLerEstoque && (montando || requisitando)),
  });
  const ordenar = (a: { readonly rotulo: string }, b: { readonly rotulo: string }) => a.rotulo.localeCompare(b.rotulo, 'pt-BR');
  const opcoesDeFornecedor = (apoioDoEstoque.data?.fornecedores ?? []).map((f) => ({ valor: f.id, rotulo: f.nome })).sort(ordenar);
  /* O item com o código e a unidade em etiqueta (31/10/2026), como o estoque. */
  const opcoesDeItem = (apoioDoEstoque.data?.produtos ?? []).map((x) => opcaoDoItem(x)).sort(ordenar);
  const opcoesDeOrdem = (apoioDoEstoque.data?.ordens ?? []).map((o) => ({ valor: o.id, rotulo: `${numeroDaOrdem(o.numero)} · ${o.equipamento}` }));
  const SEM_ESTOQUE = 'O seu perfil não lê o estoque · peça a liberação em Cadastros › Governança';
  /* ---- O que o sistema já sabe, para cada janela (pedido do dono, 31/10/2026) ---- */
  /* Montar pedido: o próximo número e o prazo que o fornecedor deu da última vez. */
  const numeroSugerido = proximoNumeroDePedido(listaDePedidos.map((p) => p.numero));
  const numero = numeroDigitado ?? numeroSugerido ?? '';
  const nomeDoFornecedor = apoioDoEstoque.data?.fornecedores.find((f) => f.id === fornecedorId)?.nome ?? '';
  const prazoSugerido = prazoDoFornecedor(listaDePedidos, nomeDoFornecedor);
  const prazoDias = prazoDigitado ?? (prazoSugerido ? String(prazoSugerido.dias) : '');
  /* Acrescentar item: o depósito de sempre, o que a necessidade manda comprar e o custo da última entrada. */
  const saldosDoEstoque = useQuery({
    queryKey: ['estoque', 'saldos'],
    queryFn: async () => (await apiDoEstoque.saldos()).saldos,
    enabled: pendente?.tipo === 'item' && podeLerEstoque,
  });
  const livroDoItem = useQuery({
    queryKey: ['estoque', 'movimentos', itemProdutoId, null],
    queryFn: async () => (await apiDoEstoque.movimentos({ produtoId: itemProdutoId })).movimentos,
    enabled: pendente?.tipo === 'item' && podeLerEstoque && itemProdutoId !== '',
  });
  const produtoDoItem = apoioDoEstoque.data?.produtos.find((x) => x.id === itemProdutoId);
  const depositoSugeridoDoItem = apoioDoEstoque.data
    ? depositoSugerido({
        tipo: 'pedido',
        produtoId: itemProdutoId,
        depositos: apoioDoEstoque.data.depositos,
        saldos: saldosDoEstoque.data ?? [],
        ultimo: ultimoDeposito,
      })
    : null;
  const itemDepositoId = itemDepositoEscolhido ?? depositoSugeridoDoItem?.valor ?? '';
  const daNecessidade = itens.find((i) => i.produtoId === itemProdutoId && i.comprar > 0) ?? null;
  const itemQuantidade = itemQuantidadeDigitada ?? (daNecessidade ? formatarAteDuasCasas(daNecessidade.comprar) : '');
  const precoSugerido = produtoDoItem ? custoSugerido(ultimaEntrada(livroDoItem.data ?? []).custo, produtoDoItem.unidade, null) : null;
  const itemPreco = itemPrecoDigitado ?? precoSugerido?.valor ?? '';
  const itemQuantidadeLida = interpretarNumero(itemQuantidade);
  const itemPrecoLido = itemPreco.trim() === '' ? null : interpretarNumero(itemPreco);
  const subtotal = totalDaLinha(itemQuantidadeLida, itemPrecoLido);
  const alcada = pendente?.tipo === 'aprovar' ? itensDoPedidoPendente.data?.alcada : undefined;
  const cabecalhoDoPendente = pendente?.tipo === 'aprovar' ? itensDoPedidoPendente.data?.pedido : undefined;
  const alcanca = alcada?.alcanca ?? true;
  const jaEnviado = pendente?.tipo === 'aprovar' && pendente.pedido.situacao === 'aguardando aprovacao';

  /* Recebimento: com um item só a chegar, ele já vem escolhido (a escolha
     única, `lib/escolha-unica`); a quantidade vem com o que falta. */
  const aChegar = (itensDoPedidoPendente.data?.itens ?? []).filter((i) => i.falta > 0);
  const unicoAChegar = pendente?.tipo === 'receber' && aChegar.length === 1 ? aChegar[0] : undefined;
  const itemId = itemEscolhidoId || (unicoAChegar?.itemId ?? '');
  const itemRecebido = itensDoPedidoPendente.data?.itens.find((i) => i.itemId === itemId) ?? null;
  const embalagemRecebida = itemRecebido?.conversoes?.find((c) => c.id === conversaoRecebida) ?? null;
  const quantidade = quantidadeDigitada ?? (itemRecebido && !embalagemRecebida ? formatarAteDuasCasas(itemRecebido.falta) : '');
  const quantidadeLida = interpretarNumero(quantidade);

  return (
    <div className="min-w-0 flex-1 overflow-auto">
      <div className="pagina flex flex-col gap-6">
        <CabecalhoPagina
          sobretitulo="Compras e reposição"
          titulo="O que falta comprar"
          subtitulo="A demanda das preventivas e do mínimo de estoque, descontado o que já está em pedido"
          acoes={
            <>
              {/* Secundário à esquerda do primário (manual, p.21). Registrar
                  necessidade é frequente, mas não é a ação que a tela existe
                  para provocar — essa é montar o pedido. */}
              {podeRequisitar ? (
                <Botao nivel="secundario" onClick={() => setRequisitando(true)}>
                  Registrar necessidade
                </Botao>
              ) : null}
            {podeMontarPedido ? (
            <JanelaDeAcao
              rotulo="Montar pedido"
              titulo="Montar um pedido de compra"
              nivel="primario"
              aberta={montando}
              aoAbrir={() => setMontando(true)}
              aoFechar={() => setMontando(false)}
              acoes={
                <>
                  <Botao nivel="terciario" onClick={() => setMontando(false)}>
                    Cancelar
                  </Botao>
                  <Botao
                    nivel="primario"
                    disabled={numero.trim() === '' || fornecedorId.trim() === '' || criarPedido.isPending}
                    onClick={() => criarPedido.mutate()}
                  >
                    {criarPedido.isPending ? 'Montando…' : 'Montar pedido'}
                  </Botao>
                </>
              }
            >
              <div className="flex flex-col gap-4" id={`${base}-pedido`}>
                <p className="txt-apoio text-tinta-secundaria">
                  O pedido nasce em rascunho, e rascunho não desconta da necessidade. Acrescente os
                  itens, peça a aprovação e só então ele sai para o fornecedor.
                </p>
                <CampoComSugestao
                  rotulo="Número do pedido"
                  valor={numero}
                  aoMudar={setNumeroDigitado}
                  preenchido={numeroDigitado === null && numeroSugerido !== null}
                  ajuda={
                    numeroDigitado === null && numeroSugerido !== null
                      ? 'O próximo depois do último pedido. Troque se o pedido tem outro número.'
                      : undefined
                  }
                  validar={(v) => (v.trim() === '' ? 'Informe o número do pedido, como PC-0012.' : null)}
                />
                <CampoSelecao
                  rotulo="Fornecedor"
                  valor={fornecedorId}
                  aoMudar={setFornecedorId}
                  opcoes={opcoesDeFornecedor}
                  rotuloVazio={rotuloDaEscolha({
                    consulta: apoioDoEstoque,
                    pode: podeLerEstoque,
                    quantas: opcoesDeFornecedor.length,
                    escolha: 'Escolha o fornecedor',
                    nenhuma: 'Nenhum fornecedor em uso · cadastre em Cadastros › Fornecedores',
                    semPermissao: SEM_ESTOQUE,
                  })}
                />
                <CampoComSugestao
                  rotulo="Prazo em dias"
                  opcional
                  unidade="dias"
                  valor={prazoDias}
                  aoMudar={setPrazoDigitado}
                  preenchido={prazoDigitado === null && prazoSugerido !== null}
                  ajuda={
                    prazoDigitado === null && prazoSugerido !== null
                      ? `O prazo que ${nomeDoFornecedor} deu no pedido ${prazoSugerido.pedido}.`
                      : 'É o prazo do fornecedor, e é ele que diz o que precisa ser antecipado.'
                  }
                  validar={(v) => {
                    const n = interpretarNumero(v);
                    return v.trim() !== '' && (n === null || n < 0) ? 'Escreva o prazo em dias, com algarismos: 30.' : null;
                  }}
                />
                {criarPedido.isError ? (
                  <p role="alert" className="txt-apoio text-parada-texto">
                    {explicarFalha(criarPedido.error).comoResolver}
                  </p>
                ) : null}
              </div>
            </JanelaDeAcao>
            ) : null}
            </>
          }
        />

        {faixa === 'falhou' ? (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-4 rounded-controle border border-borda bg-rebaixada p-4">
            <span className="flex flex-col gap-1">
              <span className="txt-corpo text-tinta">A necessidade de compra não carregou.</span>
              <span className="txt-apoio text-tinta-secundaria">{explicarFalha(necessidade.error).comoResolver}</span>
            </span>
            <Botao nivel="secundario" onClick={() => void necessidade.refetch()}>
              Carregar de novo
            </Botao>
          </div>
        ) : (
          <>
            {/* Carregando (p.32), nunca "0": os quatro cards já se sabem — o
                rótulo e o ícone ficam, e só o número é esqueleto. */}
            <FaixaIndicadores>
              <CardIndicador
                historia="IND-COM-01"
                rotulo="Itens a comprar"
                icone={Package}
                tom="marca"
                carregando={faixa === 'carregando'}
                valor={formatarInteiro(totais.aComprar)}
                contexto="Falta que nenhum pedido aberto cobre ainda"
              />
              <CardIndicador
                historia="IND-COM-02"
                rotulo="Críticos a comprar"
                icone={Wrench}
                tom="parada"
                carregando={faixa === 'carregando'}
                valor={formatarInteiro(totais.criticosAComprar)}
                alerta={totais.criticosAComprar > 0}
                contexto="Item marcado como crítico, dos que faltam"
              />
              <CardIndicador
                historia="IND-COM-03"
                rotulo="Já pedidos"
                icone={ClipboardList}
                tom="disponivel"
                carregando={faixa === 'carregando'}
                valor={formatarInteiro(totais.jaPedidos)}
                contexto="Falta que um pedido aberto já cobre — não comprar outra vez"
              />
              <CardIndicador
                historia="IND-COM-04"
                rotulo="Comprometido"
                icone={CircleDollarSign}
                tom="neutro"
                /* O comprometido sai dos pedidos, que é outra consulta: sem
                   ela, o card mostraria R$ 0,00 como se nada estivesse pedido. */
                carregando={faixa === 'carregando' || pedidos.isPending}
                valor={formatarReais(totais.comprometido)}
                contexto="Valor estimado dos pedidos aprovados e emitidos"
              />
            </FaixaIndicadores>

            {/* A CONTA, como a rota a manda — sem reescrever. */}
            {necessidade.data === undefined ? (
              <Carregando oQue="Carregando a necessidade de compra">
                <Barra className="my-1 h-3 w-1/2" />
              </Carregando>
            ) : (
              <>
                <p className="txt-apoio text-pretty text-tinta-secundaria">{necessidade.data.conta}</p>
                {/* A PREMISSA DA PREVISÃO (0091), como a rota a manda: a janela,
                    de onde vem o prazo e por que a preventiva não conta duas vezes. */}
                {necessidade.data.premissaDoConsumo ? (
                  <p className="txt-apoio text-pretty text-tinta-secundaria">{necessidade.data.premissaDoConsumo}</p>
                ) : null}
              </>
            )}
          </>
        )}

        <Tabela
          rotulo="Necessidade líquida de compra"
          colunas={COLUNAS}
          linhas={fatia.visiveis}
          chaveDaLinha={(i) => i.produtoId}
          carregando={necessidade.isPending}
          erro={
            necessidade.isError ? (
              <ErroDaLista oQue="Os itens da necessidade" erro={necessidade.error} aoTentarDeNovo={() => void necessidade.refetch()} />
            ) : null
          }
          alturaDupla
          vazio={
            <EstadoVazio
              titulo="Nada a comprar com a demanda de hoje"
              descricao="Nenhum item está abaixo do mínimo, e as preventivas que vencem logo e o consumo previsto até o próximo recebimento têm o material em estoque ou já em pedido."
            />
          }
          rodape={
            <RodapeTabela
              primeiro={fatia.primeiro}
              ultimo={fatia.ultimo}
              total={itens.length}
              pagina={fatia.pagina}
              paginas={fatia.paginas}
              porPagina={porPagina}
              opcoesPorPagina={OPCOES_POR_PAGINA}
              aoMudarPagina={setPagina}
              aoMudarPorPagina={(novo) => {
                setPorPagina(novo);
                setPagina(1);
              }}
            />
          }
        />

        <h2 className="txt-secao text-tinta">Pedidos</h2>
        <div ref={quadroDosPedidos} className="contents">
          <Tabela
            rotulo="Pedidos de compra"
            colunas={COLUNAS_DE_PEDIDO}
            linhas={paginaDosPedidos.visiveis}
            selecionada={pedidoDoEndereco}
            rodape={<RodapeTabela {...paginaDosPedidos.rodape} />}
            chaveDaLinha={(p) => p.id}
            carregando={pedidos.isPending}
            erro={
              pedidos.isError ? (
                <ErroDaLista oQue="Os pedidos" erro={pedidos.error} aoTentarDeNovo={() => void pedidos.refetch()} />
              ) : null
            }
            alturaDupla
            acoes={(p) => (acoesDoPedido(p).length > 0 ? <MaisAcoes acoes={acoesDoPedido(p)} /> : null)}
            vazio={
              <EstadoVazio
                titulo="Nenhum pedido montado"
                descricao="Monte um pedido para o que falta comprar. Ele nasce em rascunho e só desconta da necessidade depois de aprovado."
                acao={
                  permissoes.includes('pedido.gravar') ? (
                    <Botao nivel="secundario" onClick={() => setMontando(true)}>
                      Montar pedido
                    </Botao>
                  ) : undefined
                }
              />
            }
          />
        </div>
      </div>

      <Janela
        titulo="Registrar uma necessidade"
        aberta={requisitando && podeRequisitar}
        aoFechar={() => setRequisitando(false)}
        acoes={
          <>
            <Botao nivel="terciario" onClick={() => setRequisitando(false)}>
              Cancelar
            </Botao>
            <Botao
              nivel="secundario"
              disabled={
                reqProdutoId.trim() === ''
                || interpretarNumero(reqQuantidade) === null
                || (reqOrigem === 'ordem_servico' && reqOrdemId.trim() === '')
                || requisitar.isPending
              }
              onClick={() => requisitar.mutate()}
            >
              {requisitar.isPending ? 'Registrando…' : 'Registrar necessidade'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            Para o que não vem do mínimo nem da preventiva — a peça que alguém viu gastando. A
            origem fica escrita porque &ldquo;por que compramos isso&rdquo; não tem resposta três
            meses depois sem ela.
          </p>
          <CampoSelecao
            rotulo="Item"
            valor={reqProdutoId}
            aoMudar={setReqProdutoId}
            opcoes={opcoesDeItem}
            rotuloVazio={rotuloDaEscolha({
              consulta: apoioDoEstoque,
              pode: podeLerEstoque,
              quantas: opcoesDeItem.length,
              escolha: 'Escolha o item',
              nenhuma: 'Nenhum item em uso no estoque · cadastre em Estoque',
              semPermissao: SEM_ESTOQUE,
            })}
          />
          <CampoTexto
            rotulo={(() => {
              const unidade = apoioDoEstoque.data?.produtos.find((x) => x.id === reqProdutoId)?.unidade;
              return unidade ? `Quantidade, em ${unidade}` : 'Quantidade';
            })()}
            valor={reqQuantidade}
            aoMudar={setReqQuantidade}
            validar={(v) => {
              const n = interpretarNumero(v);
              return v.trim() !== '' && (n === null || n <= 0) ? 'Informe a quantidade com algarismos, maior que zero: 12 ou 2,5.' : null;
            }}
          />
          <CampoSelecao
            rotulo="De onde vem a necessidade"
            valor={reqOrigem}
            aoMudar={setReqOrigem}
            opcoes={ORIGENS.map((o) => ({ valor: o, rotulo: origemEscrita(o) }))}
          />
          {/* A O.S. só aparece quando a origem é ela: pedir sempre convidaria a
              preencher um número que não tem relação com a necessidade. */}
          {reqOrigem === 'ordem_servico' ? (
            <>
              <CampoSelecao
                rotulo="Ordem de serviço"
                valor={reqOrdemId}
                aoMudar={setReqOrdemId}
                opcoes={opcoesDeOrdem}
                rotuloVazio={rotuloDaEscolha({
                  consulta: apoioDoEstoque,
                  pode: podeLerEstoque,
                  quantas: opcoesDeOrdem.length,
                  escolha: 'Escolha a O.S.',
                  nenhuma: 'Nenhuma O.S. aberta · abra uma na Oficina, ou escolha outra origem',
                  semPermissao: SEM_ESTOQUE,
                })}
              />
              <p className="txt-ajuda">Obrigatória para esta origem: origem que não se pode conferir não é origem.</p>
            </>
          ) : null}
          <CampoDeData
            rotulo="Precisa até"
            valor={reqPrecisaAte}
            aoMudar={setReqPrecisaAte}
            opcional
            ajuda="É a data que diz o que precisa ser antecipado."
          />
          <CampoTexto rotulo="Justificativa" valor={reqJustificativa} aoMudar={setReqJustificativa} opcional />
          {requisitar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(requisitar.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Aprovar o pedido"
        descricao="Confira o valor e a sua alçada antes de decidir."
        aberta={pendente?.tipo === 'aprovar'}
        aoFechar={fecharPendente}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharPendente}>
              Voltar
            </Botao>
            <Botao
              nivel="secundario"
              disabled={aprovar.isPending || enviar.isPending}
              onClick={() => {
                if (pendente?.tipo === 'aprovar') setPendente({ tipo: 'ajuste', pedido: pendente.pedido });
              }}
            >
              Pedir ajuste
            </Botao>
            {alcanca ? (
              <Botao
                nivel="primario"
                disabled={aprovar.isPending || !itensDoPedidoPendente.data}
                onClick={() => {
                  if (pendente?.tipo === 'aprovar') aprovar.mutate(pendente.pedido.id);
                }}
              >
                {aprovar.isPending
                  ? 'Aprovando…'
                  : pendente?.tipo === 'aprovar'
                    ? `Aprovar ${formatarReais(pendente.pedido.valorEstimado)}`
                    : 'Aprovar pedido'}
              </Botao>
            ) : (
              <Botao
                nivel="primario"
                disabled={enviar.isPending || jaEnviado}
                onClick={() => {
                  if (pendente?.tipo === 'aprovar') enviar.mutate(pendente.pedido.id);
                }}
              >
                {jaEnviado ? 'Já enviado' : enviar.isPending ? 'Enviando…' : 'Enviar para aprovação'}
              </Botao>
            )}
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {pendente?.tipo === 'aprovar' ? (
            <dl className="grid grid-cols-[repeat(auto-fill,minmax(min(var(--campo-min),100%),1fr))] gap-x-6 gap-y-3">
              <div className="flex flex-col gap-1">
                <dt className="txt-legenda font-normal text-tinta-secundaria">Pedido</dt>
                <dd className="txt-corpo text-tinta">
                  <span className="codigo">{pendente.pedido.numero}</span>
                  {` · ${pendente.pedido.fornecedor}`}
                </dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="txt-legenda font-normal text-tinta-secundaria">Valor estimado</dt>
                <dd className="txt-corpo numero text-tinta">{formatarReais(pendente.pedido.valorEstimado)}</dd>
              </div>
              <div className="flex flex-col gap-1">
                <dt className="txt-legenda font-normal text-tinta-secundaria">Itens</dt>
                <dd className="txt-corpo numero text-tinta">{formatarInteiro(pendente.pedido.itens)}</dd>
              </div>
            </dl>
          ) : null}
          {cabecalhoDoPendente?.situacao === 'rascunho' && cabecalhoDoPendente.motivo ? (
            <p className="txt-apoio text-pretty text-tinta-secundaria">
              {`Ajuste pedido${cabecalhoDoPendente.por ? ` por ${cabecalhoDoPendente.por}` : ''}: ${cabecalhoDoPendente.motivo}`}
            </p>
          ) : null}
          {/* A alçada de quem está logado, ANTES do botão (p.44). */}
          {itensDoPedidoPendente.isPending && pendente?.tipo === 'aprovar' ? (
            <Carregando oQue="Carregando a sua alçada" className="flex flex-col gap-2">
              <span aria-hidden="true" className="txt-rotulo text-tinta">Sua alçada</span>
              <Barra className="h-3 w-2/3" />
            </Carregando>
          ) : itensDoPedidoPendente.isError && pendente?.tipo === 'aprovar' ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {`Não foi possível ler a sua alçada. ${explicarFalha(itensDoPedidoPendente.error).comoResolver}`}
            </p>
          ) : alcada ? (
            <div className="flex flex-col gap-1">
              <span className="txt-rotulo text-tinta">Sua alçada</span>
              <p className={`txt-corpo text-pretty ${alcanca ? 'text-tinta' : 'text-atencao-texto'}`}>
                {textoDaAlcada(alcada)}
                {alcanca ? '' : ' Este pedido passa dela: envie para quem pode aprovar, e o sino avisa.'}
              </p>
            </div>
          ) : null}
          {jaEnviado && !alcanca ? (
            <p className="txt-apoio text-tinta-secundaria">Este pedido já foi enviado e espera quem tem alçada.</p>
          ) : null}
          {aprovar.isError || enviar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(aprovar.error ?? enviar.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Acrescentar item"
        descricao={pendente?.tipo === 'item' ? `Ao pedido ${pendente.pedido.numero}, de ${pendente.pedido.fornecedor}.` : ''}
        aberta={pendente?.tipo === 'item'}
        aoFechar={fecharPendente}
        largura="m"
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharPendente}>
              Voltar
            </Botao>
            <Botao
              nivel="primario"
              disabled={
                acrescentar.isPending ||
                itemProdutoId === '' ||
                itemDepositoId === '' ||
                itemQuantidadeLida === null ||
                itemQuantidadeLida <= 0 ||
                (itemPreco.trim() !== '' && itemPrecoLido === null)
              }
              onClick={() => {
                if (pendente?.tipo !== 'item' || itemQuantidadeLida === null) return;
                acrescentar.mutate({
                  pedidoId: pendente.pedido.id,
                  produtoId: itemProdutoId,
                  depositoId: itemDepositoId,
                  quantidade: itemQuantidadeLida,
                  preco: itemPrecoLido,
                });
              }}
            >
              {acrescentar.isPending ? 'Acrescentando…' : 'Acrescentar item'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {apoioDoEstoque.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {`A lista de itens não carregou. ${explicarFalha(apoioDoEstoque.error).comoResolver}`}
            </p>
          ) : !apoioDoEstoque.data ? (
            <Carregando oQue="Carregando os itens do estoque" className="flex flex-col gap-4">
              {['Item do estoque', 'Depósito onde entra'].map((rotulo) => (
                <span key={rotulo} className="flex flex-col gap-2">
                  <span aria-hidden="true" className="txt-rotulo-campo">{rotulo}</span>
                  <Barra className="h-[var(--controle-m)] w-full rounded-controle" />
                </span>
              ))}
            </Carregando>
          ) : apoioDoEstoque.data.produtos.length === 0 ? (
            <EstadoVazio
              titulo="Nenhum item no estoque"
              descricao="O pedido leva itens do cadastro do estoque. Cadastre o item em Estoque › Cadastrar item e volte aqui."
            />
          ) : (
            <>
              <CampoSelecao
                rotulo="Item do estoque"
                valor={itemProdutoId}
                rotuloVazio="Escolha o item"
                opcoes={apoioDoEstoque.data.produtos.map((x) => opcaoDoItem(x))}
                aoMudar={(valor) => {
                  setItemProdutoId(valor);
                  /* Outro item, outra necessidade e outro custo: voltam a ser os do sistema. */
                  setItemQuantidade(null);
                  setItemPreco(null);
                }}
              />
              {/* Sem depósito cadastrado, "Acrescentar item" não tem como
                  habilitar: o campo diz por quê e a quem pedir (QAS, 20/10). */}
              <Campo
                id={`${base}-item-dep`}
                rotulo="Depósito onde entra"
                preenchido={itemDepositoEscolhido === null && depositoSugeridoDoItem !== null}
                erro={apoioDoEstoque.data.depositos.length === 0 ? faltaDoDeposito(0, permissoes.includes('estoque.gerir')) : null}
                {...(itemDepositoEscolhido === null && depositoSugeridoDoItem ? { ajuda: depositoSugeridoDoItem.ajuda } : {})}
              >
                <ListaDeEscolha
                  id={`${base}-item-dep`}
                  rotulo="Depósito onde entra"
                  valor={itemDepositoId}
                  rotuloVazio={opcaoVaziaDoDeposito(apoioDoEstoque.data.depositos.length, permissoes.includes('estoque.gerir'))}
                  opcoes={apoioDoEstoque.data.depositos.map((d) => ({
                    valor: d.id,
                    rotulo: d.nome,
                    etiquetas: [{ texto: d.codigo, tom: 'informacao' as const, titulo: 'Código' }],
                  }))}
                  aoMudar={setItemDepositoId}
                  idDoErro={apoioDoEstoque.data.depositos.length === 0 ? `${base}-item-dep-erro` : undefined}
                />
              </Campo>
            </>
          )}
          <CampoComSugestao
            rotulo="Quantidade"
            unidade={produtoDoItem?.unidade ?? ''}
            valor={itemQuantidade}
            aoMudar={setItemQuantidade}
            preenchido={itemQuantidadeDigitada === null && daNecessidade !== null}
            ajuda={
              itemQuantidadeDigitada === null && daNecessidade
                ? `O que a necessidade manda comprar: ${formatarAteDuasCasas(daNecessidade.comprar)} ${daNecessidade.unidade}.`
                : undefined
            }
            validar={(v) => {
              const n = interpretarNumero(v);
              return v.trim() !== '' && (n === null || n <= 0) ? 'Informe a quantidade com algarismos, maior que zero: 12 ou 2,5.' : null;
            }}
          />
          <CampoComSugestao
            rotulo="Preço unitário"
            unidade="R$"
            valor={itemPreco}
            aoMudar={setItemPreco}
            opcional
            preenchido={itemPrecoDigitado === null && precoSugerido !== null}
            ajuda={[
              itemPrecoDigitado === null && precoSugerido
                ? precoSugerido.ajuda.replace('Último custo', 'Último custo de entrada')
                : 'Sem preço, o item fica fora do valor estimado, e quem aprova vê o aviso.',
              subtotal === null ? null : `Subtotal: ${formatarReais(subtotal)}.`,
            ]
              .filter(Boolean)
              .join(' ')}
            validar={(v) => (v.trim() !== '' && interpretarNumero(v) === null ? 'Escreva o preço com algarismos: 320,50.' : null)}
          />
          {acrescentar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(acrescentar.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Pedir ajuste do pedido"
        descricao="O pedido volta ao rascunho, e quem o montou recebe o aviso com o que mudar."
        aberta={pendente?.tipo === 'ajuste'}
        aoFechar={fecharPendente}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharPendente}>
              Voltar
            </Botao>
            <Botao
              nivel="secundario"
              disabled={motivo.trim().length < 10 || ajustar.isPending}
              onClick={() => {
                if (pendente?.tipo === 'ajuste') ajustar.mutate({ id: pendente.pedido.id, motivo: motivo.trim() });
              }}
            >
              {ajustar.isPending ? 'Devolvendo…' : 'Devolver para ajuste'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="txt-corpo text-pretty text-tinta">
            {pendente?.tipo === 'ajuste'
              ? `Devolver o pedido ${pendente.pedido.numero}, de ${pendente.pedido.fornecedor}, para ajuste?`
              : ''}
          </p>
          <CampoTexto
            rotulo="O que precisa mudar"
            valor={motivo}
            aoMudar={setMotivo}
            maxLength={500}
            ajuda="Pelo menos 10 caracteres. Vai no aviso para quem montou o pedido e fica na trilha."
          />
          {ajustar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(ajustar.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Cancelar o pedido"
        aberta={pendente?.tipo === 'cancelar'}
        aoFechar={fecharPendente}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharPendente}>
              Voltar
            </Botao>
            <Botao
              nivel="secundario"
              disabled={motivo.trim().length < 15 || cancelar.isPending}
              onClick={() => {
                if (pendente?.tipo === 'cancelar') cancelar.mutate({ id: pendente.pedido.id, motivo });
              }}
            >
              {cancelar.isPending ? 'Cancelando…' : 'Cancelar pedido'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <p className="txt-corpo text-pretty text-tinta">
            {pendente?.tipo === 'cancelar'
              ? `Cancelar o pedido ${pendente.pedido.numero}, de ${pendente.pedido.fornecedor}? A necessidade que ele cobria volta a aparecer na lista de compras.`
              : ''}
          </p>
          <CampoTexto
            rotulo="Motivo"
            valor={motivo}
            aoMudar={setMotivo}
            ajuda="Pelo menos 15 caracteres. É este texto que explica, amanhã, por que o item voltou a faltar."
          />
          {cancelar.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(cancelar.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>

      <Janela
        titulo="Registrar o recebimento"
        aberta={pendente?.tipo === 'receber'}
        aoFechar={fecharPendente}
        acoes={
          <>
            <Botao nivel="terciario" onClick={fecharPendente}>
              Cancelar
            </Botao>
            <Botao
              nivel="secundario"
              disabled={itemId === '' || quantidadeLida === null || receber.isPending}
              onClick={() => {
                if (quantidadeLida === null) return;
                receber.mutate({
                  itemId,
                  quantidade: quantidadeLida,
                  documento,
                  custo: custoUnitario.trim() === '' ? null : interpretarNumero(custoUnitario),
                  conversaoId: embalagemRecebida?.id ?? null,
                });
              }}
            >
              {receber.isPending ? 'Registrando…' : 'Registrar recebimento'}
            </Botao>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          {/* A peça entra no LIVRO DO ESTOQUE, e o aviso está aqui porque é
              onde a dúvida aparece: o recebimento não é um saldo paralelo. */}
          <p className="txt-apoio text-pretty text-tinta-secundaria">
            O que chegar entra no estoque, no depósito escolhido quando o item foi pedido. Receber
            parcial é normal: o que falta continua pendente no pedido, e o pedido só fecha quando
            nada falta.
          </p>

          {itensDoPedidoPendente.isPending ? (
            /* Carregando (p.32): a legenda é certa; os itens, na forma da linha
               de escolha — o botão de rádio e o texto —, são esqueleto. */
            <div className="flex flex-col gap-2">
              <span className="txt-rotulo text-tinta">Item que chegou</span>
              <Carregando oQue="Carregando os itens do pedido" className="flex flex-col gap-2">
                {['w-1/2', 'w-2/5'].map((largura) => (
                  <span key={largura} className="flex items-center gap-2 py-1">
                    <Barra className="size-4 rounded-pill" />
                    <Barra className={`h-3 ${largura}`} />
                  </span>
                ))}
              </Carregando>
            </div>
          ) : itensDoPedidoPendente.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(itensDoPedidoPendente.error).comoResolver}
            </p>
          ) : (
            <fieldset className="flex flex-col gap-2 border-0 p-0">
              <legend className="txt-rotulo text-tinta">Item que chegou</legend>
              {(itensDoPedidoPendente.data?.itens ?? [])
                .filter((i) => i.falta > 0)
                .map((i) => (
                  <label key={i.itemId} className="flex items-baseline gap-2">
                    <input
                      type="radio"
                      name={`${base}-item`}
                      value={i.itemId}
                      checked={itemId === i.itemId}
                      onChange={() => {
                        setItemId(i.itemId);
                        setQuantidade(null);
                        setConversaoRecebida('');
                      }}
                    />
                    <span className="txt-corpo text-tinta">
                      {`${i.codigo} · ${i.produto}`}
                    </span>
                    <span className="txt-apoio text-tinta-secundaria">
                      {`faltam ${formatarAteDuasCasas(i.falta)} ${i.unidade} de ${formatarAteDuasCasas(i.pedida)}`}
                    </span>
                  </label>
                ))}
              {(itensDoPedidoPendente.data?.itens ?? []).every((i) => i.falta <= 0) ? (
                <p className="txt-apoio text-tinta-secundaria">
                  Todos os itens deste pedido já chegaram.
                </p>
              ) : null}
            </fieldset>
          )}

          {itemRecebido && (itemRecebido.conversoes ?? []).length > 0 ? (
            /* 0088 · o item chega em caixa ou balde: a quantidade pode ser na
               embalagem, e o banco converte para a unidade do item. */
            <CampoSelecao
              rotulo="Unidade em que chegou"
              valor={conversaoRecebida}
              rotuloVazio={`${itemRecebido.unidade} (unidade do item)`}
              opcoes={(itemRecebido.conversoes ?? []).map((c) => ({ valor: c.id, rotulo: textoDaConversao(c, itemRecebido.unidade) }))}
              aoMudar={(v) => {
                setConversaoRecebida(v);
                setQuantidade('');
              }}
            />
          ) : null}
          <CampoTexto
            rotulo={embalagemRecebida ? `Quantidade recebida, em ${embalagemRecebida.unidade}` : 'Quantidade recebida'}
            valor={quantidade}
            aoMudar={setQuantidade}
            ajuda={
              embalagemRecebida && itemRecebido
                ? ajudaDaConversao(quantidadeLida, embalagemRecebida, itemRecebido.unidade)
                : 'Vem preenchida com o que falta. Receber a mais do que foi pedido é recusado.'
            }
          />
          <CampoTexto rotulo="Documento" valor={documento} aoMudar={setDocumento} ajuda="O número da nota, opcional." />
          <HistoriaNaMesa codigo="CMP-COM-06" className="min-w-0">
            <CampoTexto
              rotulo={embalagemRecebida ? `Custo por ${embalagemRecebida.unidade}` : 'Custo unitário'}
              valor={custoUnitario}
              aoMudar={setCustoUnitario}
              ajuda={
                itemRecebido?.precoUnitario != null
                  ? `Opcional. Escreva o custo da nota: é ele que chega ao custo por máquina. O preço estimado no pedido foi ${formatarReais(itemRecebido.precoUnitario)} por ${itemRecebido.unidade}.`
                  : 'Opcional. Escreva o custo da nota: é ele que chega ao custo por máquina, e não o preço estimado do pedido.'
              }
            />
          </HistoriaNaMesa>
          {receber.isError ? (
            <p role="alert" className="txt-apoio text-parada-texto">
              {explicarFalha(receber.error).comoResolver}
            </p>
          ) : null}
        </div>
      </Janela>
    </div>
  );
}

/**
 * O CAMPO DE TEXTO QUE O SISTEMA JÁ PREENCHEU (31/10/2026): o número do
 * pedido, o prazo do fornecedor, a quantidade da necessidade, o custo da
 * última entrada. É o `CampoTexto` com a marca "· pré-preenchido" e a
 * unidade ao lado do número, em cinza (p.23); a ajuda diz de onde o valor
 * veio. O erro aparece ao sair do campo, como no resto do sistema.
 */
function CampoComSugestao({
  rotulo,
  valor,
  aoMudar,
  preenchido,
  ajuda,
  validar,
  opcional = false,
  unidade = '',
}: {
  readonly rotulo: string;
  readonly valor: string;
  readonly aoMudar: (valor: string) => void;
  readonly preenchido: boolean;
  readonly ajuda?: string | undefined;
  readonly validar?: (valor: string) => string | null;
  readonly opcional?: boolean;
  readonly unidade?: string;
}) {
  const id = useId();
  const [erro, setErro] = useState<string | null>(null);
  return (
    <Campo id={id} rotulo={rotulo} opcional={opcional} preenchido={preenchido} erro={erro} {...(ajuda ? { ajuda } : {})}>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="text"
          inputMode={unidade ? 'decimal' : 'text'}
          autoComplete="off"
          className="controle w-full"
          value={valor}
          onChange={(evento) => {
            setErro(null);
            aoMudar(evento.target.value);
          }}
          onBlur={() => {
            setErro(validar ? validar(valor) : null);
          }}
          {...(erro ? { 'aria-invalid': true as const, 'aria-describedby': `${id}-erro` } : {})}
        />
        {unidade ? <span className="txt-corpo shrink-0 text-tinta-apagada">{unidade}</span> : null}
      </div>
    </Campo>
  );
}
